<template>
  <div class="orcamentos-table">
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :endpoint="endpoint"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :autoLoad="false"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @row-click="handleRowClick"
      @edit-click="handleEditClick"
      @button-click="handleButtonClick"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAgendarOrcamento } from '../../composables/useAgendarOrcamento';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';

type Id = number | string;

interface OrcamentoRow {
  ID?: Id;
  status?: string;
  status_label?: string;
  pode_agendar?: number | string;
  agendar_motivos?: string[];
  cliente_id?: Id;
  [key: string]: unknown;
}

interface CellParams {
  value: unknown;
  data: OrcamentoRow;
  node?: { rowPinned?: string };
}

interface ButtonClickPayload {
  value?: Id;
  data?: OrcamentoRow;
  action?: string;
}

interface DataTableInstance {
  loadData: () => void;
}

// Emits
const emit = defineEmits<{
  (e: 'edit-orcamento', id: Id): void;
  (e: 'cliente.edit', id: Id): void;
  (e: 'schedule-orcamento', id: Id): void;
}>();

// Composables
const { t } = useI18n();
const { handleAgendar, motivosTexto } = useAgendarOrcamento();

// Reactive data
const datatableRef = ref<DataTableInstance | null>(null);

// Computed
const endpoint = computed(() => '/index.php/orcamentos/buscaDados');

// DataTable Configuration
const datatableConfig = computed(() => {
  return {
    tableId: 'orcamentosTable',
    pageLength: 25,
    pagination: true,
    columns: tableColumns.value,
    rowTestId: (data: Record<string, unknown>) => {
      const id = data?.orcamento_id ?? data?.ID;
      return id == null ? '' : `orcamento-row-${id}`;
    },
  };
});

const filterConfig = computed(() => {
  return {
    columns: {
      'ID': 'text',
      'status_label': 'select',
      'orcamento_data': 'text',
      'franquia': 'select',
      'nome_cliente': 'text',
      'rua': 'text',
      'bairro': 'select',
      'Localidade': 'select',
      'total': 'range',
      'origem': 'select'
    }
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const tableColumns = computed(() => {
  return [
    {
      data: 'ID',
      title: t('ID'),
      renderType: 'clickable_id'
    },
    {
      data: 'status_label',
      title: t('Status'),
      cellRenderer: (params: CellParams): string => {
        // For pinned summary row, show raw value
        if (params.node && params.node.rowPinned) {
          return params.value != null ? String(params.value) : '';
        }

        let severity = '';
        let icon = '';

        switch (params.data.status) {
          case 'Pendente':
            severity = 'warning';
            icon = 'exclamation-triangle';
            break;
          case 'Respondido':
            severity = 'info';
            icon = 'clock';
            break;
          case 'Aprovado':
            severity = 'success';
            icon = 'check';
            break;
          case 'Parcialmente concluído':
            severity = 'info';
            icon = 'check';
            break;
          case 'Todos os serviços realizados':
            severity = 'primary';
            icon = 'flag';
            break;
          case 'Cancelado':
            severity = 'secondary';
            icon = 'times';
            break;
          default:
            severity = 'secondary';
            icon = 'question';
        }

        const dica = params.data.status === 'Pendente'
          ? ` title="${t('Aguardando aprovação: clique em Agendar para enviar à agenda')}"`
          : '';
        return `<span class='p-tag p-tag-${severity}'${dica}><i class='pi pi-${icon} p-tag-icon'></i> <span class="p-tag-value">${params.value}</span></span>`;
      }
    },
    {
      data: 'ID',
      title: t('Agendar'),
      renderType: 'clickable_button',
      buttonText: t('Agendar'),
      buttonIcon: 'pi pi-calendar',
      buttonClass: 'p-button-success',
      buttonAction: 'schedule',
      buttonTestId: (rowData: OrcamentoRow) => `agendar-orcamento-${rowData.ID}-btn`,
      // Desabilitado com o motivo, nunca escondido (chamado DS 2026-0068).
      buttonDisabled: (rowData: OrcamentoRow) => Number(rowData.pode_agendar) !== 1,
      buttonTooltip: (rowData: OrcamentoRow) => (Number(rowData.pode_agendar) === 1 ? '' : motivosTexto(rowData.agendar_motivos))
    },
    {
      data: 'orcamento_data',
      title: t('Data'),
      renderType: 'datetime'  // AG Grid will handle date formatting automatically
    },
    {
      data: 'franquia',
      title: t('Franquia')
    },
    {
      data: 'nome_cliente',
      title: t('Cliente'),
      renderType: 'custom_clickable_id',
      clickCallback: (_value: unknown, data: OrcamentoRow) => {
        // Call the client edit handler
        if (data.cliente_id !== undefined) handleClientEditClick(data.cliente_id);
      }
    },
    {
      data: 'rua',
      title: t('Rua')
    },
    {
      data: 'bairro',
      title: t('Bairro')
    },
    {
      data: 'Localidade',
      title: t('Local')
    },
    {
      data: 'total',
      title: t('Total'),
      renderType: 'currency',
      footerType: 'sum'
    },
    {
      data: 'origem',
      title: t('Origem')
    }
  ];
});

// Methods
const loadData = (): void => {
  log('OrcamentosTable', 'loadData', 'Triggering table refresh');
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown): void => {
  const rows = Array.isArray(data) ? data : [];
  log('OrcamentosTable', 'handleDataLoaded', 'Data loaded', rows.length, 'rows');
};

const handleDataError = (error: unknown): void => {
  log('OrcamentosTable', 'handleDataError', 'Error loading data', error);
  eventBus.emit('globalMessage.error', t('Erro ao carregar orçamentos'));
};

const handleRowClick = (event: unknown): void => {
  const rowData = event as OrcamentoRow | undefined;
  log('OrcamentosTable', 'handleRowClick', 'Row clicked', rowData?.ID || null);
};

const handleEditClick = (orcamentoId: Id): void => {
  log('OrcamentosTable', 'handleEditClick', 'Edit clicked', orcamentoId);
  emit('edit-orcamento', orcamentoId);
};

const handleClientEditClick = (clienteId: Id): void => {
  log('OrcamentosTable', 'handleClientEditClick', 'Client edit clicked', clienteId);

  // Emit to eventBus for global modal
  eventBus.emit('cliente.edit', clienteId);

  // Also emit to parent component (for backward compatibility)
  emit('cliente.edit', clienteId);
};

const handleButtonClick = (buttonData?: ButtonClickPayload): void => {
  log('OrcamentosTable', 'handleButtonClick', 'Button clicked', buttonData);

  if (buttonData?.action === 'schedule' && buttonData.data?.ID !== undefined) {
    // Use the shared composable for scheduling
    handleAgendar(Number(buttonData.data.ID));
  }
};

// Stable event handler refs for cleanup
const handleFilterUpdated = (): void => {
  log('OrcamentosTable', 'event:filter.ready', 'Received');
  loadData();
};

const handleOrcamentoSaved = (): void => {
  log('OrcamentosTable', 'event:orcamento.saved', 'Received');
  loadData();
};

const handleClienteSaved = (): void => {
  log('OrcamentosTable', 'event:cliente.saved', 'Received');
  loadData();
};

// Lifecycle
onMounted(async () => {
  log('OrcamentosTable', 'mounted', 'Component mounted, loading initial data');

  // Load initial data
  await loadData();

  // Listen for events
  eventBus.on('filter.updated', handleFilterUpdated);
  eventBus.on('orcamento.saved', handleOrcamentoSaved);
  eventBus.on('cliente.saved', handleClienteSaved);
});

onBeforeUnmount(() => {
  log('OrcamentosTable', 'beforeUnmount', 'Component unmounting');

  // Clean up event listeners
  eventBus.off('filter.updated', handleFilterUpdated);
  eventBus.off('orcamento.saved', handleOrcamentoSaved);
  eventBus.off('cliente.saved', handleClienteSaved);
});

// Expose methods
defineExpose({
  loadData
});
</script>

<style scoped>
.orcamentos-table {
  margin-top: 20px;
  margin-bottom: 20px;
}
</style>
